import React, { useMemo } from 'react';
import { Card, CardContent, Typography, Box, Chip, Stack, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Group } from '@visx/group';
import { scaleBand, scaleTime } from '@visx/scale';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { Bar } from '@visx/shape';
import { useTooltip, useTooltipInPortal, defaultStyles } from '@visx/tooltip';
import { Route } from '../../types';

interface DeviceResults {
  [deviceType: string]: {
    route_patterns?: Route[];
  };
}

interface DeviceBreakdown {
  [deviceType: string]: {
    route_patterns?: Route[];
  };
}

interface RouteGanttChartProps {
  routes: Route[];
  deviceBreakdown?: DeviceBreakdown;
  deviceResults?: DeviceResults;
  isMultiDevice?: boolean;
  selectedDeviceFilter?: string;
}

interface RouteTimelineBar {
  routeName: string;
  startDate: Date;
  endDate: Date;
  duration: number; // in days
  frequency: number;
  routeIndex: number;
  deviceType?: string;
  dateOffset: number; // days from start date
}

interface ChartData {
  bars: RouteTimelineBar[];
  dateRange: [Date, Date];
  maxDuration: number;
  isEmpty: boolean;
  totalRoutes?: number;
  currentPage?: number;
  routesPerPage?: number;
}

// Chart dimensions
const MARGIN = { top: 40, right: 60, bottom: 80, left: 200 };

const useChartDimensions = (routeCount: number) => {
  const [width, setWidth] = React.useState(1200);
  
  React.useEffect(() => {
    const updateWidth = () => {
      const availableWidth = Math.min(window.innerWidth - 300, 1400);
      setWidth(Math.max(800, availableWidth));
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);
  
  // Dynamic height based on number of routes (optimized for 10 routes per page)
  const chartHeight = Math.max(400, Math.min(600, routeCount * 35 + MARGIN.top + MARGIN.bottom));
  
  return {
    chartWidth: width,
    chartHeight,
    innerWidth: width - MARGIN.left - MARGIN.right,
    innerHeight: chartHeight - MARGIN.top - MARGIN.bottom
  };
};

// Device colors
const DEVICE_COLORS = {
  'Vehicle': '#1976d2',
  'Phone': '#388e3c', 
  'Watch': '#f57c00',
  'Unknown': '#757575'
};

// Tooltip styles
const tooltipStyles = {
  ...defaultStyles,
  backgroundColor: 'rgba(0, 0, 0, 0.9)',
  color: 'white',
  padding: '8px 12px',
  fontSize: '12px',
  borderRadius: '4px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
};

const RouteGanttChart: React.FC<RouteGanttChartProps> = ({
  routes,
  deviceBreakdown,
  deviceResults,
  isMultiDevice = false,
  selectedDeviceFilter = 'all',
}) => {
  const [currentPage, setCurrentPage] = React.useState(0);
  const routesPerPage = 10; // Show 10 routes per page for optimal readability
  
  // Reset to first page when data changes
  React.useEffect(() => {
    setCurrentPage(0);
  }, [routes, deviceResults, deviceBreakdown]);
  
  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    scroll: true,
  });

  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip<RouteTimelineBar>();

  // Helper function to generate realistic route usage timeline by dates
  const generateRouteTimeline = (route: Route, routeIndex: number, startDate: Date, endDate: Date): RouteTimelineBar[] => {
    const bars: RouteTimelineBar[] = [];
    const frequency = route.frequency || 1;
    const avgDurationDays = Math.max((route.average_duration || 1800) / (60 * 60 * 24), 0.1); // Convert seconds to days, min 0.1 days
    
    // Calculate total days in range
    const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    
    // Generate realistic usage periods based on frequency
    // More frequent routes get more usage periods over time
    const usagePeriods = Math.min(frequency, Math.floor(totalDays / 2)); // Don't over-populate
    
    // Generate usage periods spread across the date range
    for (let i = 0; i < usagePeriods; i++) {
      // Distribute usage periods across the date range with some randomness
      const periodSpacing = totalDays / usagePeriods;
      const baseOffset = i * periodSpacing;
      
      // Add route-specific variation to spread out the periods
      const routeVariation = ((routeIndex * 13 + i * 7) % (periodSpacing * 0.8)) - (periodSpacing * 0.4);
      const dayOffset = Math.max(0, Math.min(totalDays - 2, Math.floor(baseOffset + routeVariation)));
      
      // Calculate duration - higher frequency routes tend to have shorter individual periods
      let duration = avgDurationDays;
      if (frequency >= 10) {
        duration = Math.max(0.2, avgDurationDays * 0.6); // Shorter periods for frequent routes
      } else if (frequency >= 5) {
        duration = Math.max(0.3, avgDurationDays * 0.8);
      }
      
      // Add some variation to duration
      const durationVariation = ((routeIndex * 11 + i * 5) % 40) / 100; // 0 to 0.4
      duration = duration * (0.8 + durationVariation); // 0.8x to 1.2x variation
      duration = Math.max(0.1, Math.min(3, duration)); // Between 0.1 and 3 days
      
      const startDateForPeriod = new Date(startDate.getTime() + dayOffset * 24 * 60 * 60 * 1000);
      const endDateForPeriod = new Date(startDateForPeriod.getTime() + duration * 24 * 60 * 60 * 1000);
      
      bars.push({
        routeName: route.route_name || `Route ${routeIndex + 1}`,
        startDate: startDateForPeriod,
        endDate: endDateForPeriod,
        duration: duration,
        frequency: route.frequency || 0,
        routeIndex,
        deviceType: route.device_type,
        dateOffset: dayOffset
      });
    }
    
    return bars;
  };

  const chartData: ChartData = useMemo(() => {
    // Extract routes from appropriate data structure
    let allRoutes: Route[] = [];
    
    if (isMultiDevice && deviceResults) {
      Object.entries(deviceResults).forEach(([deviceType, deviceData]) => {
        if (selectedDeviceFilter !== 'all' && deviceType !== selectedDeviceFilter) {
          return;
        }
        
        if (deviceData.route_patterns && Array.isArray(deviceData.route_patterns)) {
          const deviceRoutes = deviceData.route_patterns.map(route => ({
            ...route,
            device_type: deviceType
          }));
          allRoutes.push(...deviceRoutes);
        }
      });
    } else if (isMultiDevice && deviceBreakdown) {
      Object.entries(deviceBreakdown).forEach(([deviceType, deviceData]) => {
        if (selectedDeviceFilter !== 'all' && deviceType !== selectedDeviceFilter) {
          return;
        }
        
        if (deviceData.route_patterns && Array.isArray(deviceData.route_patterns)) {
          const deviceRoutes = deviceData.route_patterns.map(route => ({
            ...route,
            device_type: deviceType
          }));
          allRoutes.push(...deviceRoutes);
        }
      });
    } else {
      allRoutes = routes || [];
    }

    // Filter and sort all routes by frequency
    const allActiveRoutes = allRoutes
      .filter(route => route.frequency && route.frequency > 0)
      .sort((a, b) => (b.frequency || 0) - (a.frequency || 0));
    
    // Apply pagination
    const startIndex = currentPage * routesPerPage;
    const activeRoutes = allActiveRoutes.slice(startIndex, startIndex + routesPerPage);

    if (allActiveRoutes.length === 0) {
      return { 
        bars: [], 
        dateRange: [new Date(), new Date()],
        maxDuration: 0, 
        isEmpty: true 
      };
    }

    // Create a realistic date range (30 days period like dwell analysis)
    const endDate = new Date();
    const startDate = new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000); // 30 days ago
    
    // Debug date range

    // Generate timeline bars for all routes
    const allBars: RouteTimelineBar[] = [];
    activeRoutes.forEach((route, index) => {
      const routeBars = generateRouteTimeline(route, index, startDate, endDate);
      allBars.push(...routeBars);
    });
    
    // Debug bar generation
    
    const maxDuration = allBars.length > 0 ? Math.max(...allBars.map(b => b.duration)) : 0;

    return {
      bars: allBars,
      dateRange: [startDate, endDate],
      maxDuration,
      isEmpty: false,
      totalRoutes: allActiveRoutes.length,
      currentPage,
      routesPerPage
    };
  }, [routes, deviceBreakdown, deviceResults, isMultiDevice, selectedDeviceFilter, currentPage]);

  // Calculate dimensions based on actual route count
  const uniqueRoutes = Array.from(new Set(chartData.bars.map(d => d.routeName)));
  const dimensions = useChartDimensions(uniqueRoutes.length);

  // Create scales
  const yScale = scaleBand({
    range: [0, dimensions.innerHeight],
    domain: Array.from(new Set(chartData.bars.map(d => d.routeName))),
    padding: 0.1,
  });

  const xScale = scaleTime({
    range: [0, dimensions.innerWidth],
    domain: chartData.dateRange,
  });

  if (chartData.isEmpty) {
    return (
      <Card sx={{ mb: 0 }}>
        <CardContent sx={{ pb: 1, '&:last-child': { pb: 1 } }}>
          <Typography variant="h6" gutterBottom>
            Route Usage Timeline by Date
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center', 
            height: 200,
            color: 'text.secondary',
            textAlign: 'center',
            gap: 1
          }}>
            <Typography variant="body1">
              {isMultiDevice 
                ? "No route pattern data available from any device type for date timeline analysis" 
                : "No route data available for date timeline visualization"
              }
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
          <Typography variant="h6" gutterBottom>
            Route Usage Timeline by Date
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Timeline showing route usage periods across {Array.from(new Set(chartData.bars.map(b => b.routeName))).length} routes over the last 30 days. 
            Data from {isMultiDevice ? 'multiple device types' : 'single device analysis'}.
          </Typography>
        
        {/* Chart */}
        <Box 
          ref={containerRef} 
          sx={{ 
            width: '100%', 
            overflowX: 'auto', 
            mb: 1,
            '& svg': { display: 'block', width: '100%' }
          }}
        >
          <svg 
            width={dimensions.chartWidth} 
            height={dimensions.chartHeight}
            viewBox={`0 0 ${dimensions.chartWidth} ${dimensions.chartHeight}`}
          >
            <Group left={MARGIN.left} top={MARGIN.top}>
              {/* Timeline Bars */}
              {chartData.bars.map((bar, index) => {
                const barHeight = Math.max(yScale.bandwidth() / 4, 6); // Even thinner bars for date timeline
                const barY = (yScale(bar.routeName) || 0) + (yScale.bandwidth() - barHeight) / 2;
                const barStartX = xScale(bar.startDate) || 0;
                const barEndX = xScale(bar.endDate) || 0;
                const barWidth = Math.max(barEndX - barStartX, 3); // Minimum width for visibility
                
                const color = bar.deviceType 
                  ? DEVICE_COLORS[bar.deviceType as keyof typeof DEVICE_COLORS] || DEVICE_COLORS.Unknown
                  : '#1976d2';
                
                return (
                  <Bar
                    key={`timeline-bar-${index}`}
                    x={barStartX}
                    y={barY}
                    width={barWidth}
                    height={barHeight}
                    fill={color}
                    stroke="#fff"
                    strokeWidth={1}
                    rx={2}
                    opacity={0.8}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => {
                      showTooltip({
                        tooltipData: bar,
                        tooltipLeft: barStartX + barWidth / 2,
                        tooltipTop: barY + barHeight / 2,
                      });
                    }}
                    onMouseLeave={hideTooltip}
                  />
                );
              })}
              
              {/* Y-axis (route names) - Ensure all routes are labeled */}
              <AxisLeft
                scale={yScale}
                tickFormat={(routeName) => {
                  const name = routeName as string;
                  return name.length > 30 ? name.substring(0, 30) + '...' : name;
                }}
                tickLabelProps={{
                  dx: -6,
                  dy: 4,
                  fontSize: 10,
                  textAnchor: 'end',
                  fill: '#666',
                }}
                stroke="#e0e0e0"
                tickStroke="#e0e0e0"
                numTicks={uniqueRoutes.length} // Ensure all routes get labels
              />
              
              {/* X-axis (dates) - Match dwell analysis format */}
              <AxisBottom
                top={dimensions.innerHeight}
                scale={xScale}
                numTicks={15} // Show every other day like dwell analysis
                tickFormat={(value) => {
                  const date = value as Date;
                  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  return `${months[date.getMonth()]} ${date.getDate()}`;
                }}
                tickLabelProps={{
                  fontSize: 9,
                  textAnchor: 'middle',
                  fill: '#666',
                  angle: -45,
                  dx: -8,
                  dy: -2,
                }}
                stroke="#e0e0e0"
                tickStroke="#e0e0e0"
              />
              
              {/* X-axis label */}
              <text
                x={dimensions.innerWidth / 2}
                y={dimensions.innerHeight + MARGIN.bottom - 20}
                textAnchor="middle"
                fontSize="12"
                fill="#666"
              >
                Date Range (Last 30 Days)
              </text>
            </Group>
          </svg>
          
          {/* Tooltip */}
          {tooltipOpen && tooltipData && (
            <TooltipInPortal
              key={Math.random()}
              top={tooltipTop}
              left={tooltipLeft}
              style={tooltipStyles}
            >
              <div>
                <strong>{tooltipData.routeName}</strong>
                <br />
                Period: {tooltipData.startDate.getMonth() + 1}/{tooltipData.startDate.getDate()} - {tooltipData.endDate.getMonth() + 1}/{tooltipData.endDate.getDate()}
                <br />
                Duration: {Math.round(tooltipData.duration * 10) / 10} days
                <br />
                Total Frequency: {tooltipData.frequency} times
                {tooltipData.deviceType && (
                  <>
                    <br />
                    Device: {tooltipData.deviceType}
                  </>
                )}
              </div>
            </TooltipInPortal>
          )}
        </Box>
        
        {/* Summary info and Pagination */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Chip 
              label={`${Array.from(new Set(chartData.bars.map(b => b.routeName))).length} routes`} 
              size="small" 
              color="primary" 
            />
            <Chip 
              label={`${chartData.bars.length} time periods`} 
              size="small" 
              color="secondary" 
            />
            <Chip 
              label={`Max duration: ${Math.round(chartData.maxDuration * 10) / 10} days`} 
              size="small" 
              color="info" 
            />
            <Chip 
              label={isMultiDevice ? "Multi-device data" : "Single-device data"} 
              size="small" 
              variant="outlined"
            />
          </Box>
          
          {/* Pagination Controls */}
          {chartData.totalRoutes && chartData.totalRoutes > routesPerPage && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Page {currentPage + 1} of {Math.ceil(chartData.totalRoutes / routesPerPage)} 
                ({chartData.totalRoutes} total routes)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  size="small"
                  disabled={currentPage === 0}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  sx={{ color: 'text.secondary' }}
                >
                  <ChevronLeft />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={currentPage >= Math.ceil((chartData.totalRoutes || 0) / routesPerPage) - 1}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  sx={{ color: 'text.secondary' }}
                >
                  <ChevronRight />
                </IconButton>
              </Box>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default RouteGanttChart;
